/** Shared chip ids / parsers for property admin list pages (Policies, Claims, Buildings, Flagged cancellations). */

import { getPolicyIssuedDateRangeFilterLabel } from './policyIssuedDateRangeFilter';
import { formatBooleanFlagChipLabel } from './formatBooleanYesNo';

export function normalizeStringArray(value) {
  if (!Array.isArray(value)) return [];
  return value.filter((v) => v != null && String(v).trim() !== '');
}

function formatDateRangeChipSubtitle(range) {
  const startDate = range?.startDate;
  const endDate = range?.endDate;
  if (!startDate && !endDate) return '';
  const fmt = (d) => {
    try {
      const t = new Date(d).getTime();
      if (!Number.isFinite(t)) return String(d);
      return new Date(d).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return String(d);
    }
  };
  const parts = [];
  if (startDate) parts.push(`Start ${fmt(startDate)}`);
  if (endDate) parts.push(`End ${fmt(endDate)}`);
  return parts.join(' — ');
}

export const POLICY_LEASE_CHIP_ID = 'lease';
export const POLICY_ISSUED_CHIP_ID = 'issued';
export const CLAIM_CREATED_CHIP_ID = 'created';

const POLICY_EXPIRING_LABELS = {
  lt_30: 'Within 30 days',
  '31_60': '31–60 days',
  '61_90': '61–90 days',
  '91_120': '91–120 days',
};

const POLICY_LEASE_BUCKET_LABELS = {
  past: 'Past',
  current: 'Current',
  future: 'Future',
};

export function buildPolicyListFilterChips({
  selectedAppType,
  selectedIssuedDate,
  selectedCoverageStatus,
  selectedLeaseDate,
  selectedTenantCount,
  activeLease,
  selectedExpiringRange,
  selectedLeaseBucket,
  appTypeOptions,
  coverageStatusOptions,
}) {
  const chips = [];
  const appTypeText = (v) => appTypeOptions.find((o) => o.value === v)?.text ?? String(v ?? '');
  const coverageStatusText = (v) =>
    coverageStatusOptions.find((o) => o.value === v)?.text ?? String(v ?? '');

  normalizeStringArray(selectedAppType).forEach((v) => {
    chips.push({ id: `appType:${v}`, label: `Application Type: ${appTypeText(v)}` });
  });

  const issuedLabel = getPolicyIssuedDateRangeFilterLabel(selectedIssuedDate);
  if (issuedLabel) {
    chips.push({
      id: POLICY_ISSUED_CHIP_ID,
      label: `Issued Date: ${issuedLabel}`,
    });
  }

  normalizeStringArray(selectedCoverageStatus).forEach((v) => {
    chips.push({
      id: `coverageStatus:${v}`,
      label: `Policy Status: ${coverageStatusText(v)}`,
    });
  });

  const leaseSubtitle = formatDateRangeChipSubtitle(selectedLeaseDate);
  if (leaseSubtitle) {
    chips.push({
      id: POLICY_LEASE_CHIP_ID,
      label: `Lease: ${leaseSubtitle}`,
    });
  }

  normalizeStringArray(selectedTenantCount).forEach((v) => {
    chips.push({ id: `tenant:${v}`, label: `Total Resident: ${v}` });
  });

  normalizeStringArray(activeLease).forEach((v) => {
    const pretty = formatBooleanFlagChipLabel(v);
    chips.push({ id: `activeLease:${v}`, label: `Active lease: ${pretty}` });
  });

  if (selectedExpiringRange) {
    const label = POLICY_EXPIRING_LABELS[selectedExpiringRange] || selectedExpiringRange;
    chips.push({
      id: `expiring:${selectedExpiringRange}`,
      label: `Expiring: ${label}`,
    });
  }

  if (selectedLeaseBucket) {
    const label = POLICY_LEASE_BUCKET_LABELS[selectedLeaseBucket] || selectedLeaseBucket;
    chips.push({
      id: `leaseBucket:${selectedLeaseBucket}`,
      label: `Lease bucket: ${label}`,
    });
  }

  return chips;
}

export function parsePolicyListFilterChipId(chipId) {
  if (chipId === POLICY_LEASE_CHIP_ID) return { type: 'lease' };
  if (chipId === POLICY_ISSUED_CHIP_ID) return { type: 'issued' };
  if (chipId.startsWith('expiring:')) {
    return { type: 'expiring', value: chipId.slice('expiring:'.length) };
  }
  if (chipId.startsWith('leaseBucket:')) {
    return { type: 'leaseBucket', value: chipId.slice('leaseBucket:'.length) };
  }
  const idx = chipId.indexOf(':');
  if (idx < 1) return null;
  return { type: chipId.slice(0, idx), value: chipId.slice(idx + 1) };
}

export function buildClaimListFilterChips({
  selectedFileStatus,
  selectedAppType,
  selectedLeaseDate,
  fileStatusOptions,
  appTypeOptions,
}) {
  const chips = [];
  const statusText = (v) => fileStatusOptions.find((o) => o.value === v)?.text ?? String(v ?? '');
  const appTypeText = (v) => appTypeOptions.find((o) => o.value === v)?.text ?? String(v ?? '');

  normalizeStringArray(selectedFileStatus).forEach((v) => {
    chips.push({ id: `fileStatus:${v}`, label: `Claim file status: ${statusText(v)}` });
  });

  normalizeStringArray(selectedAppType).forEach((v) => {
    chips.push({ id: `appType:${v}`, label: `Application Type: ${appTypeText(v)}` });
  });

  const createdSubtitle = formatDateRangeChipSubtitle(selectedLeaseDate);
  if (createdSubtitle) {
    chips.push({
      id: CLAIM_CREATED_CHIP_ID,
      label: `Created: ${createdSubtitle}`,
    });
  }

  return chips;
}

export function parseClaimListFilterChipId(chipId) {
  if (chipId === CLAIM_CREATED_CHIP_ID) return { type: 'created' };
  const idx = chipId.indexOf(':');
  if (idx < 1) return null;
  return { type: chipId.slice(0, idx), value: chipId.slice(idx + 1) };
}

const BUILDING_TYPE_LABELS = {
  'Event Process;Auto Enroll': 'Guarantor Waiver+LDR',
  'Auto Enroll': 'Rent Guaranty',
};

export function buildPropertyListFilterChips({
  selectedBType,
  isSHouse,
  isDispositioned,
  unitFilter,
}) {
  const chips = [];
  normalizeStringArray(selectedBType).forEach((v) => {
    const label = BUILDING_TYPE_LABELS[v] || v;
    chips.push({
      id: `propertyType:${encodeURIComponent(v)}`,
      label: `Property type: ${label}`,
    });
  });

  normalizeStringArray(isSHouse).forEach((v) => {
    const pretty = formatBooleanFlagChipLabel(v);
    chips.push({ id: `studentHousing:${v}`, label: `Student housing: ${pretty}` });
  });

  normalizeStringArray(isDispositioned).forEach((v) => {
    const pretty = formatBooleanFlagChipLabel(v);
    chips.push({ id: `dispositioned:${v}`, label: `Dispositioned: ${pretty}` });
  });

  if (unitFilter === 'active' || unitFilter === 'inactive') {
    chips.push({
      id: `unit:${unitFilter}`,
      label: `Units: ${unitFilter === 'active' ? 'Active' : 'Inactive'}`,
    });
  }

  return chips;
}

export function parsePropertyListFilterChipId(chipId) {
  const typePrefix = chipId.startsWith('propertyType:')
    ? 'propertyType:'
    : chipId.startsWith('buildingType:')
      ? 'buildingType:'
      : null;
  if (typePrefix) {
    const enc = chipId.slice(typePrefix.length);
    try {
      return { type: 'propertyType', value: decodeURIComponent(enc) };
    } catch {
      return { type: 'propertyType', value: enc };
    }
  }
  const idx = chipId.indexOf(':');
  if (idx < 1) return null;
  return { type: chipId.slice(0, idx), value: chipId.slice(idx + 1) };
}

export function buildFlaggedCancellationListFilterChips({
  selectedFlg,
  selectedAppType,
  selectedPolicyStatus,
  flagOptions,
  appTypeOptions,
  policyStatusOptions,
}) {
  const chips = [];
  const flagText = (v) => flagOptions.find((o) => o.value === v)?.text ?? String(v ?? '');
  const appTypeText = (v) => appTypeOptions.find((o) => o.value === v)?.text ?? String(v ?? '');
  const policyText = (v) => policyStatusOptions.find((o) => o.value === v)?.text ?? String(v ?? '');

  normalizeStringArray(selectedFlg).forEach((v) => {
    chips.push({ id: `flag:${v}`, label: `Flag: ${flagText(v)}` });
  });

  normalizeStringArray(selectedAppType).forEach((v) => {
    chips.push({ id: `appType:${v}`, label: `Application Type: ${appTypeText(v)}` });
  });

  normalizeStringArray(selectedPolicyStatus).forEach((v) => {
    chips.push({ id: `policyStatus:${v}`, label: `Policy status: ${policyText(v)}` });
  });

  return chips;
}

export function parseFlaggedCancellationListFilterChipId(chipId) {
  const idx = chipId.indexOf(':');
  if (idx < 1) return null;
  return { type: chipId.slice(0, idx), value: chipId.slice(idx + 1) };
}
